import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Typography } from 'antd'
import { Icon } from '@iconify/react'
import { debounce } from 'lodash'
import { PRIVATE_ROUTES } from '../../../routes'
import CapacityFilters from './components/CapacityFilters'
import CapacitySkeleton from './components/CapacitySkeleton'
import CapacitySummary from './components/CapacitySummary'
import CapacityTable from './components/CapacityTable'
import CapacityTimeline from './components/CapacityTimeline'
import {
    fetchProjectsForClientModal,
    clientsState,
} from '../../clients/clients.slice'
import {
    capacityState,
    fetchCapacityData,
    resetCapacityFilters,
    setCapacityFilters,
} from './capacity.slice'
import { getDefaultCapacityRange } from './capacity.utils'
import styles from './ProjectCapacityDashboard.module.css'

const { Title } = Typography

const ProjectCapacityDashboard = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const {
        status,
        error,
        users,
        summary,
        search,
        projectIds,
        statusFilter,
        start,
        end,
    } = useSelector(capacityState)
    const {
        clientModalProjectsListData = [],
        clientModalProjectPage,
        clientModalProjectSearch,
        clientModalIsNext,
        clientModalListStatus,
    } = useSelector(clientsState)

    const isLoading = status === 'loading'

    useEffect(() => {
        dispatch(fetchCapacityData())
    }, [dispatch])

    const debouncedProjectSearch = useMemo(
        () =>
            debounce((value) => {
                dispatch(
                    fetchProjectsForClientModal({
                        skip: 0,
                        filter: value ? { search: value } : {},
                        limit: 10,
                        search: value,
                    })
                )
            }, 300),
        [dispatch]
    )

    useEffect(() => () => debouncedProjectSearch.cancel(), [debouncedProjectSearch])

    const handleProjectFilterOpen = useCallback(
        (open) => {
            if (!open) return
            dispatch(
                fetchProjectsForClientModal({
                    skip: 0,
                    filter: {},
                    limit: 10,
                    search: '',
                })
            )
        },
        [dispatch]
    )

    const handleProjectFilterScroll = useCallback(
        (e) => {
            const { target } = e
            if (
                target.scrollHeight - target.scrollTop <=
                    target.clientHeight + 50 &&
                clientModalIsNext &&
                clientModalListStatus !== 'loading'
            ) {
                const nextPage = clientModalProjectPage + 1
                const skip = (nextPage - 1) * 10
                dispatch(
                    fetchProjectsForClientModal({
                        skip,
                        filter: clientModalProjectSearch
                            ? { search: clientModalProjectSearch }
                            : {},
                        limit: 10,
                        search: clientModalProjectSearch,
                    })
                )
            }
        },
        [
            dispatch,
            clientModalIsNext,
            clientModalListStatus,
            clientModalProjectPage,
            clientModalProjectSearch,
        ]
    )

    const debouncedSearchFetch = useMemo(
        () =>
            debounce(() => {
                dispatch(fetchCapacityData())
            }, 350),
        [dispatch]
    )

    useEffect(() => () => debouncedSearchFetch.cancel(), [debouncedSearchFetch])

    const refetch = useCallback(() => {
        dispatch(fetchCapacityData())
    }, [dispatch])

    const applyFilters = useCallback(
        (patch) => {
            dispatch(setCapacityFilters(patch))
            dispatch(fetchCapacityData())
        },
        [dispatch]
    )

    const handleReset = useCallback(() => {
        const defaultRange = getDefaultCapacityRange()
        dispatch(resetCapacityFilters())
        dispatch(
            setCapacityFilters({
                start: defaultRange.start,
                end: defaultRange.end,
            })
        )
        dispatch(fetchCapacityData())
    }, [dispatch])

    const handleDateRangeChange = useCallback(
        (range) => {
            if (!range?.start || !range?.end) return
            applyFilters({
                start: range.start,
                end: range.end,
            })
        },
        [applyFilters]
    )

    const showEmpty = status === 'loaded' && users.length === 0

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <button
                    type="button"
                    className={styles.backLink}
                    onClick={() => navigate(PRIVATE_ROUTES.reporting)}
                >
                    <Icon icon="mdi:arrow-left" width={18} height={18} />
                    All reports
                </button>
                <Title level={3} className={styles.title}>
                    Project Capacity Dashboard
                </Title>
                <p className={styles.subtitle}>
                    Resource availability and workload over time across projects
                    and tasks
                </p>
            </header>

            <CapacityFilters
                search={search}
                projectIds={projectIds}
                projects={clientModalProjectsListData}
                statusFilter={statusFilter}
                start={start}
                end={end}
                onSearchChange={(value) => {
                    dispatch(setCapacityFilters({ search: value }))
                    debouncedSearchFetch()
                }}
                onProjectChange={(value) =>
                    applyFilters({ projectIds: value || [] })
                }
                onProjectSearch={debouncedProjectSearch}
                onProjectPopupScroll={handleProjectFilterScroll}
                projectListLoading={clientModalListStatus === 'loading'}
                projectListPage={clientModalProjectPage}
                onProjectOpenChange={handleProjectFilterOpen}
                onStatusChange={(value) =>
                    applyFilters({ statusFilter: value })
                }
                onDateRangeChange={handleDateRangeChange}
                onReset={handleReset}
                onExport={() => {}}
            />

            <div className={styles.content}>
                {isLoading ? <CapacitySkeleton /> : null}

                {status === 'error' ? (
                    <div className={styles.errorState}>
                        <p className={styles.errorMessage}>{error}</p>
                        <button
                            type="button"
                            className={styles.retryBtn}
                            onClick={refetch}
                        >
                            Try again
                        </button>
                    </div>
                ) : null}

                {!isLoading && status !== 'error' && showEmpty ? (
                    <div className={styles.emptyStateWrap}>
                        <div className={styles.emptyIconBadge} aria-hidden>
                            <Icon
                                icon="mdi:chart-timeline-variant"
                                width={28}
                                height={28}
                                className={styles.emptyIcon}
                            />
                        </div>
                        <p className={styles.emptyTitle}>No allocation found</p>
                        <p className={styles.emptyHint}>
                            No team capacity matches your current filters. Try
                            expanding the date range or clearing project and
                            status filters.
                        </p>
                        <button
                            type="button"
                            className={styles.emptyAction}
                            onClick={handleReset}
                        >
                            <Icon icon="mdi:filter-off-outline" width={16} />
                            Reset filters
                        </button>
                    </div>
                ) : null}

                {!isLoading && status !== 'error' && !showEmpty ? (
                    <>
                        <CapacitySummary summary={summary} />
                        <CapacityTimeline
                            users={users}
                            rangeStart={start}
                            rangeEnd={end}
                        />
                        <CapacityTable
                            users={users}
                            rangeStart={start}
                            rangeEnd={end}
                        />
                    </>
                ) : null}
            </div>
        </div>
    )
}

export default ProjectCapacityDashboard
